class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      const picker = this.querySelector('.ls-cart-variant-picker[open]');
      if (picker) { picker.open = false; picker.querySelector('summary').focus({ preventScroll: true }); }
      else this.close();
    });
    this.addEventListener('click', this.onRecommendationClick.bind(this));
    this.addEventListener('change', this.onUpsellVariantChange.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.busyObserver = new MutationObserver(() => this.syncBusyState());
    this.busyObserver.observe(this, { attributes: true, attributeFilter: ['aria-busy', 'class'] });
    this.syncBusyState();
    this.onHistoryPageShow = this.onHistoryPageShow.bind(this);
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.onHistoryPageShow);
    if (document.readyState === 'complete') this.onHistoryPageShow({ persisted: false });
  }

  disconnectedCallback() {
    this.busyObserver?.disconnect();
    window.removeEventListener('pageshow', this.onHistoryPageShow);
  }

  onHistoryPageShow(event) {
    const navigation = window.performance?.getEntriesByType('navigation')[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    this.historyCartStale = true;
    this.refreshHistoryCart();
  }

  refreshHistoryCart() {
    if (this.historyCartRefresh) return this.historyCartRefresh;
    const items = this.querySelector('cart-drawer-items');
    if (!items?.onCartUpdate) return Promise.resolve(false);
    this.historyCartRefresh = Promise.resolve()
      .then(() => items.onCartUpdate({ throwOnError: true }))
      .then(() => {
        this.historyCartStale = false;
        return true;
      })
      .catch(() => false)
      .finally(() => { this.historyCartRefresh = null; });
    return this.historyCartRefresh;
  }

  syncBusyState() {
    const isBusy = this.getAttribute('aria-busy') === 'true';
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    const acceleratedCheckout = this.querySelector('.cart__dynamic-checkout-buttons');

    if (checkoutButton) {
      const shouldDisable = isBusy || this.classList.contains('is-empty');
      checkoutButton.toggleAttribute('disabled', shouldDisable);
      checkoutButton.setAttribute('aria-disabled', String(shouldDisable));
    }

    acceleratedCheckout?.toggleAttribute('inert', isBusy);
    if (isBusy) {
      acceleratedCheckout?.setAttribute('aria-busy', 'true');
    } else {
      acceleratedCheckout?.removeAttribute('aria-busy');
    }
  }

  onRecommendationClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.ls-cart-variant-picker')) {
      this.querySelectorAll('.ls-cart-variant-picker[open]').forEach(picker => { picker.open = false; });
    }
    const gift = target.closest('[data-ls-gift-wiggle]');
    if (gift && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gift.getAnimations().forEach(animation => animation.cancel());
      gift.animate([
        { transform: 'rotate(0) scale(1)' }, { transform: 'rotate(-14deg) scale(1.15)' },
        { transform: 'rotate(12deg) scale(1.15)' }, { transform: 'rotate(-7deg) scale(1.08)' },
        { transform: 'rotate(0) scale(1)' }
      ], { duration: 550, easing: 'ease-in-out' });
    }
    const choice = target.closest('[data-ls-upsell-choice]');
    if (!choice || choice.disabled) return;
    const card = choice.closest('[data-ls-cart-upsell]');
    const select = card?.querySelector('[data-ls-cart-upsell-variant]');
    const picker = choice.closest('details');
    if (!select || !picker) return;
    const positions = [];
    for (let parent = card; parent && parent !== this.parentElement; parent = parent.parentElement) {
      if (parent.scrollHeight > parent.clientHeight) positions.push([parent, parent.scrollTop]);
    }
    select.value = choice.dataset.lsUpsellChoice;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    card.querySelector('[data-ls-variant-label]').textContent = choice.textContent.trim().split(' — ')[0];
    picker.querySelectorAll('[data-ls-upsell-choice]').forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
    picker.open = false;
    picker.querySelector('summary').focus({ preventScroll: true });
    positions.forEach(([element, top]) => { element.scrollTop = top; });
  }

  onUpsellVariantChange(event) {
    const select = event.target.closest?.('[data-ls-cart-upsell-variant]');
    if (!select) return;

    const selectedOption = select.selectedOptions?.[0];
    const card = select.closest('[data-ls-cart-upsell]');
    if (!selectedOption || !card) return;

    const image = card.querySelector('[data-ls-cart-upsell-image]');
    const price = card.querySelector('[data-ls-cart-upsell-price]');
    const comparePrice = card.querySelector('[data-ls-cart-upsell-compare-price]');
    const addButton = card.querySelector('.ls-cart-upsell__add');
    const addButtonLabel = addButton?.querySelector('[data-ls-cart-upsell-add-label]');
    const available = selectedOption.dataset.available === 'true';

    card.dataset.selectedVariantId = selectedOption.value;
    if (price) price.textContent = selectedOption.dataset.price || '';
    if (comparePrice) {
      comparePrice.textContent = selectedOption.dataset.comparePrice || '';
      comparePrice.toggleAttribute('hidden', !selectedOption.dataset.comparePrice);
    }
    if (image && selectedOption.dataset.imageUrl) {
      image.removeAttribute('srcset');
      image.src = selectedOption.dataset.imageUrl;
      image.alt = selectedOption.dataset.imageAlt || '';
    }
    if (addButton) {
      addButton.toggleAttribute('disabled', !available);
      if (available) {
        addButton.removeAttribute('aria-disabled');
      } else {
        addButton.setAttribute('aria-disabled', 'true');
      }
    }
    if (addButtonLabel) {
      addButtonLabel.textContent = available ? card.dataset.addLabel : card.dataset.soldOutLabel;
    }

    const errorWrapper = card.querySelector('.product-form__error-message-wrapper');
    errorWrapper?.setAttribute('hidden', '');
    const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
    if (errorMessage) errorMessage.textContent = '';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy || this.historyCartStale) {
      const refreshed = await this.refreshHistoryCart();
      if (!refreshed) {
        window.location.assign(window.LSThemeReviewView ? `${routes.cart_url}?view=pl-review` : routes.cart_url);
        return;
      }
    }
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.ls-cart-drawer__header .drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.syncBusyState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
